/**
 * useRealtimeChat — connects to Azure Web PubSub for real-time chat.
 *
 * Works for two scenarios:
 *   1. Order chat:     group = 'order:{orderId}'
 *   2. Shop questions: group = 'question:{threadId}'
 *
 * Falls back transparently if Web PubSub is unavailable.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { apiUrl } from '@/config'
import { getGuestId } from '@/utils/guestIdentity'
import { getAuthHeaders } from '@/stores/authStore'

export interface MessagePin {
  datasetId?: string | null
  sectionIndex?: number | null
  frameIndex: number
  imagePixelX: number
  imagePixelY: number
  radius?: number | null
}

export interface ChatMsg {
  id: string
  senderRole?: string   // 'guest' | 'owner' (questions)
  senderId?: string     // (orders)
  senderName: string
  text: string
  createdAt: string
  pin?: MessagePin | null
}

export interface LocationUpdate {
  frameIndex: number
  imagePixelX?: number
  imagePixelY?: number
  point3D?: [number, number, number] | null
}

interface UseChatOptions {
  group: string                    // e.g. 'order:abc123' or 'question:xyz'
  userId?: string | null           // logged-in user id, or omit for guest
  shopId?: string                  // required for question threads (Cosmos partition key)
  initialMessages?: ChatMsg[]
  onClosed?: (ownerComment?: string) => void  // for question threads
  onLocationUpdate?: (loc: LocationUpdate) => void  // live location from the other party
}

interface UseChatReturn {
  messages: ChatMsg[]
  isConnected: boolean
  sendMessage: (text: string, role?: string, senderName?: string, pin?: MessagePin | null) => Promise<void>
  sendLocation: (loc: LocationUpdate) => void
  appendMessage: (msg: ChatMsg) => void
}

export function useRealtimeChat({
  group,
  userId,
  shopId,
  initialMessages = [],
  onClosed,
  onLocationUpdate,
}: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<ChatMsg[]>(initialMessages)
  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const groupRef = useRef(group)
  const shopIdRef = useRef(shopId)
  const onClosedRef = useRef(onClosed)
  const onLocationUpdateRef = useRef(onLocationUpdate)

  useEffect(() => { groupRef.current = group }, [group])
  useEffect(() => { shopIdRef.current = shopId }, [shopId])
  useEffect(() => { onClosedRef.current = onClosed }, [onClosed])
  useEffect(() => { onLocationUpdateRef.current = onLocationUpdate }, [onLocationUpdate])

  // Seed with initial messages when they change (e.g. thread loaded from server)
  // Compare by first+last message IDs so re-opening the chat always re-seeds,
  // even if the message count happens to be the same.
  const seedKey = initialMessages.length > 0
    ? `${initialMessages[0]?.id}|${initialMessages[initialMessages.length - 1]?.id}|${initialMessages.length}`
    : ''
  useEffect(() => {
    if (initialMessages.length > 0) {
      setMessages(initialMessages)
    }
  }, [seedKey]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------------------------------------------------------------
   * Connect to Web PubSub via negotiate endpoint
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!group) return

    let ws: WebSocket | null = null
    let closed = false

    const connect = async () => {
      try {
        const effectiveUserId = userId ?? getGuestId()
        const res = await fetch(
          apiUrl(`/api/chat/negotiate?group=${encodeURIComponent(group)}&userId=${encodeURIComponent(effectiveUserId)}`),
          { headers: { ...getAuthHeaders() } }
        )
        if (!res.ok) {
          console.warn('[useRealtimeChat] negotiate failed', res.status)
          return
        }
        const { url } = await res.json()
        if (closed) return

        ws = new WebSocket(url, 'json.webpubsub.azure.v1')
        wsRef.current = ws

        ws.onopen = () => {
          console.log('[useRealtimeChat] connected, joining group', group)
          // Must explicitly join the group — the token only grants permission,
          // it does NOT auto-join. Send joinGroup immediately after open.
          ws!.send(JSON.stringify({ type: 'joinGroup', group, ackId: 1 }))
        }

        ws.onmessage = (event) => {
          try {
            const frame = JSON.parse(event.data)
            console.log('[useRealtimeChat] frame', frame.type, frame.from ?? '', frame.dataType ?? '')
            // Azure Web PubSub json.webpubsub.azure.v1 subprotocol wraps server-sent
            // messages as: { type, from, dataType, data }. Our payload lives in .data.
            // System messages (connected, disconnected, ack) have no .data — skip those.
            if (frame.type === 'ack') {
              if (frame.success) {
                console.log('[useRealtimeChat] joined group', group)
                setIsConnected(true)
              } else {
                console.warn('[useRealtimeChat] joinGroup failed', frame)
              }
              return
            }
            // frame.data may be a parsed object (correct) or a string (double-encoded).
            // Handle both cases defensively.
            let payload = frame.data ?? frame
            if (typeof payload === 'string') {
              try { payload = JSON.parse(payload) } catch { /* not JSON */ }
            }
            if (payload.type === 'message' && payload.message) {
              console.log('[useRealtimeChat] new msg', payload.message.id, payload.message.senderRole)
              setMessages((prev) => {
                // Deduplicate by id
                if (prev.some((m) => m.id === payload.message.id)) return prev
                return [...prev, payload.message as ChatMsg]
              })
            } else if (payload.type === 'location' && payload.location) {
              // Ephemeral live-location update from the other party
              console.log('[useRealtimeChat] location update received', payload.location)
              onLocationUpdateRef.current?.(payload.location as LocationUpdate)
            } else if (payload.type === 'closed') {
              onClosedRef.current?.(payload.ownerComment)
            } else {
              console.warn('[useRealtimeChat] unhandled payload', payload)
            }
          } catch (err) {
            console.warn('[useRealtimeChat] onmessage error', err, event.data)
          }
        }

        ws.onclose = () => {
          console.log('[useRealtimeChat] disconnected from', group)
          setIsConnected(false)
        }

        ws.onerror = (err) => {
          console.warn('[useRealtimeChat] ws error', err)
          setIsConnected(false)
        }
      } catch (err) {
        console.warn('[useRealtimeChat] connect error', err)
      }
    }

    connect()

    return () => {
      closed = true
      ws?.close()
      wsRef.current = null
      setIsConnected(false)
    }
  }, [group, userId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------------------------------------------------------------
   * Send a message via REST. The server broadcasts it so we receive it
   * back through the WebSocket (deduplicated by id).
   * ------------------------------------------------------------------ */
  const sendMessage = useCallback(async (text: string, role = 'guest', senderName?: string, pin?: MessagePin | null) => {
    const g = groupRef.current
    if (!g || (!text.trim() && !pin)) return

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    }

    let res: Response | null = null

    if (g.startsWith('order:')) {
      const orderId = g.replace('order:', '')
      res = await fetch(apiUrl(`/api/orders/${orderId}/messages`), {
        method: 'POST',
        headers,
        body: JSON.stringify({ text }),
      })
    } else if (g.startsWith('question:')) {
      const threadId = g.replace('question:', '')
      const sid = shopIdRef.current
      if (!sid) { console.warn('[useRealtimeChat] shopId missing for question thread'); return }
      const body: Record<string, unknown> = { text, senderRole: role }
      if (senderName) body.senderName = senderName
      if (pin) body.pin = pin
      res = await fetch(
        apiUrl(`/api/shop-questions/${threadId}/messages?shopId=${encodeURIComponent(sid)}`),
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        }
      )
    }

    // Optimistic append: add the server-returned message immediately so it
    // shows up without waiting for the WPS echo. WPS dedup prevents duplicates.
    if (res?.ok) {
      try {
        const msg: ChatMsg = await res.json()
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev
          return [...prev, msg]
        })
      } catch { /* ignore */ }
    }
  }, [])

  /* Send ephemeral location update directly via WebSocket (no REST, no storage) */
  const sendLocation = useCallback((loc: LocationUpdate) => {
    const ws = wsRef.current
    const g = groupRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !g) {
      console.warn('[useRealtimeChat] sendLocation skipped — ws not ready', ws?.readyState, g)
      return
    }
    console.log('[useRealtimeChat] sendLocation', loc.frameIndex, g)
    ws.send(JSON.stringify({
      type: 'sendToGroup',
      group: g,
      dataType: 'json',
      data: { type: 'location', location: loc },
      noEcho: true,  // Don't echo back to sender
    }))
  }, [])

  /* Allow imperative append for optimistic updates */
  const appendMessage = useCallback((msg: ChatMsg) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev
      return [...prev, msg]
    })
  }, [])

  return { messages, isConnected, sendMessage, sendLocation, appendMessage }
}
